import { useCallback, useEffect, useState } from "react";
import type { StaffFormInitialData } from "../../../types/staffForm";

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/gif",
];
const ALLOWED_EXTENSIONS = /\.(jpe?g|png|webp|avif|gif)$/i;
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

/** A photo change staged in the form, applied by the form's Save. */
export type StaffPhotoChange =
  | { kind: "upload"; file: File }
  | { kind: "remove" }
  | null;

/** Validation for a picked photo (same rules as the former uploader). */
export function validateStaffPhoto(file: File): string | null {
  if (
    !ALLOWED_TYPES.includes(file.type) &&
    !ALLOWED_EXTENSIONS.test(file.name)
  ) {
    return "Please use a JPEG, PNG, WebP, AVIF or GIF image.";
  }
  if (file.size > MAX_SIZE_BYTES) {
    return "That image is too large. Please use one under 5MB.";
  }
  return null;
}

/**
 * The staff photo as staged in the form (like Branding → Logo): picking a file
 * shows a local preview, Remove marks the saved photo for removal, and nothing
 * is uploaded or saved until the form's Save. Closing the form discards it.
 */
export function useStaffPhoto(initialData?: StaffFormInitialData | null) {
  const initialUrl =
    initialData?.staff_photo_url ?? initialData?.avatar_url ?? null;
  const [savedUrl, setSavedUrl] = useState<string | null>(initialUrl);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSavedUrl(initialUrl);
  }, [initialUrl]);

  // A local preview for the staged file; revoked when replaced or unmounted.
  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const selectFile = useCallback((file: File) => {
    const message = validateStaffPhoto(file);
    setError(message);
    if (message) return;
    setPendingFile(file);
    setPendingRemove(false);
  }, []);

  /** Drops a staged file, or marks the saved photo for removal. */
  const remove = useCallback(() => {
    setError(null);
    if (pendingFile) {
      setPendingFile(null);
      return;
    }
    if (savedUrl) setPendingRemove(true);
  }, [pendingFile, savedUrl]);

  /** Called by the save once the change is stored on the staff record. */
  const markSaved = useCallback((url: string | null) => {
    setSavedUrl(url);
    setPendingFile(null);
    setPendingRemove(false);
  }, []);

  const change: StaffPhotoChange = pendingFile
    ? { kind: "upload", file: pendingFile }
    : pendingRemove
      ? { kind: "remove" }
      : null;

  return {
    displayUrl: pendingRemove ? null : (previewUrl ?? savedUrl),
    error,
    change,
    selectFile,
    remove,
    markSaved,
  };
}
